import React, { useState, useMemo } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import QuestionCard from './QuestionCard';
import Icon from '../../components/Icon';
import LoadingSpinner from '../../components/LoadingSpinner';

const EmployeeDashboard = ({ user, questions, setNotification }) => {
    const [view, setView] = useState('list');
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [answerContent, setAnswerContent] = useState('');

    // FIX: Safely calculate stats by checking if `questions` is null/undefined first.
    const summaryStats = useMemo(() => {
        if (!questions) {
            return { toAnswer: 0, inReview: 0, passed: 0 };
        }
        return {
            toAnswer: questions.filter(q => q.status === 'unanswered' || q.status === 'needs-revision').length,
            inReview: questions.filter(q => q.status === 'in-review').length,
            passed: questions.filter(q => q.status === 'passed').length,
        };
    }, [questions]);

    const handleSelectQuestion = (question, view) => {
        setSelectedQuestion(question);
        setAnswerContent(question.answer || '');
        setView(view);
    };

    const handleBackToList = () => {
        setView('list');
        setSelectedQuestion(null);
        setAnswerContent('');
    };

    const handleSubmitAnswer = async () => {
        if (!selectedQuestion) return;
        try {
            const questionRef = doc(db, 'questions', selectedQuestion.id);
            await updateDoc(questionRef, { answer: answerContent, status: 'in-review', answeredAt: new Date() });
            setNotification({ type: 'success', message: 'Answer submitted successfully!' });
            handleBackToList();
        } catch (error) {
            setNotification({ type: 'error', message: `Error submitting answer: ${error.message}` });
        }
    };

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image'],
            ['clean']
        ],
    };

    // FIX: Show a loading spinner while questions are being fetched (i.e., when the prop is null).
    if (questions === null) {
        return <LoadingSpinner />;
    }

    if (view === 'list') {
        return (
            <div>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-xl shadow-sm flex items-center gap-4">
                        <div className="bg-indigo-100 p-3 rounded-full"><Icon name="HelpCircle" className="text-indigo-600" /></div>
                        <div>
                            <p className="text-sm text-slate-500">To Answer</p>
                            <p className="text-2xl font-bold text-slate-800">{summaryStats.toAnswer}</p>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm flex items-center gap-4">
                        <div className="bg-amber-100 p-3 rounded-full"><Icon name="Clock" className="text-amber-600" /></div>
                        <div>
                            <p className="text-sm text-slate-500">In Review</p>
                            <p className="text-2xl font-bold text-slate-800">{summaryStats.inReview}</p>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm flex items-center gap-4">
                        <div className="bg-emerald-100 p-3 rounded-full"><Icon name="CheckCircle" className="text-emerald-600" /></div>
                        <div>
                            <p className="text-sm text-slate-500">Passed</p>
                            <p className="text-2xl font-bold text-slate-800">{summaryStats.passed}</p>
                        </div>
                    </div>
                </div>

                {/* Question List */}
                <div>
                    {questions.length > 0 ? (
                        questions.map(q => (
                            <QuestionCard key={q.id} question={q} onSelect={handleSelectQuestion} />
                        ))
                    ) : (
                        <div className="text-center py-12">
                            <Icon name="Inbox" className="mx-auto h-12 w-12 text-slate-400" />
                            <h3 className="mt-2 text-lg font-medium text-slate-800">No questions assigned</h3>
                            <p className="mt-1 text-sm text-slate-500">You're all caught up. Check back later for new questions.</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Answer or View screen
    return (
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg">
            <div className="mb-6 pb-4 border-b border-slate-200">
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">{selectedQuestion.text}</h2>
            </div>
            {view === 'answer' ? (
                <>
                    <p className="text-slate-600 mb-4">Enter your answer below.</p>
                    <ReactQuill theme="snow" value={answerContent} onChange={setAnswerContent} modules={modules} className="bg-white" />
                    <div className="mt-6 flex justify-end gap-3">
                        <button onClick={handleBackToList} className="font-semibold py-2 px-4 rounded-md bg-slate-200 hover:bg-slate-300 text-slate-800 transition-colors">Cancel</button>
                        <button onClick={handleSubmitAnswer} className="font-semibold py-2 px-4 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white transition-colors shadow-sm">Submit for Review</button>
                    </div>
                </>
            ) : ( // View mode
                <>
                    <div className="prose prose-slate max-w-none mb-6 ql-editor" dangerouslySetInnerHTML={{ __html: selectedQuestion.answer }} />
                    <button onClick={handleBackToList} className="font-semibold py-2 px-4 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white transition-colors shadow-sm">Back to List</button>
                </>
            )}
        </div>
    );
};

export default EmployeeDashboard;